"use client"

import { CalendarIcon } from "lucide-react"
import * as React from "react"
import { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverClose
} from "@/components/ui/popover"
import {
  getDaysAgo,
  getDaysPlus,
  getRangeSelectShortcutButtons,
  formatDate,
  getNow,
  getDateRangeStart
} from "@/lib/filters/date/date-utils";
import {DateRangePickerProps, DateShortcut} from "@/lib/filters/date/types";
import { cn } from "@/lib/utils"
import { useDate } from "@/stores/date-filter";

export function getTriggerButtonLabel(date: DateRange | undefined, label?: string){
  if (label) {
    return label
  }

  if (!date || !date.to || !date.from) {
    return 'Custom'
  }

  return `${formatDate(date.from, "yyyy-MM-dd")} - ${formatDate(date.to, "yyyy-MM-dd")}`
}

interface ShortcutButtonsProps { 
  presets: DateShortcut[]
  setDate: (date: DateRange) => void
}

const ShortcutButtons = ({ presets, setDate }: ShortcutButtonsProps) => {
  return presets.map(preset => {
    const handleClick = () => setDate(preset.date)

    return <Button
      data-testid={`date-filter:preset:custom:${preset.label}`}
      onClick={handleClick}
      variant={'ghost'}
      className={'w-full basis-full box-border max-h-9 h-auto font-normal text-xs text-primary'} key={`all-presets-${preset.label}`}
    >
      {preset.label}
    </Button>
  })
}

export default function DateRangePicker({
  className,
  triggerButtonClassName,
  label
}: DateRangePickerProps) {
  const { date, setDate, resetDate } = useDate();
  const [ initialDate, setInitialDate ] = React.useState<DateRange>(date)
  const [monthFrom, setMonthFrom] = React.useState<Date | undefined>(date.from)
  const [monthTo, setMonthTo] = React.useState<Date | undefined>(date.to)
  const buttonLabel = getTriggerButtonLabel(date, label)
  const shortcutButtons = getRangeSelectShortcutButtons()
  const now = getNow()
  const dateRangeStart = getDateRangeStart()
  const disabledAfter = getDaysAgo(1, date.to)
  const disabledBefore = getDaysPlus(1, date.from)

  React.useEffect(() => {
    setMonthFrom(date.from)
    setMonthTo(date.to)
  }, [date])

  const setFromDate = (selected: DateRange) => {
    if (selected) {
      setDate({
        ...date,
        from: selected?.to ? selected.to : selected.from
      })
    }
  }

  const setToDate = (selected: DateRange) => {
    if (selected) {
      setDate({
        ...date,
        to: selected?.from ? selected.from : selected.to
      })
    }
  }

  const handleCustomButtonClick = () => setInitialDate(date)
  const handleCalendarFromMonthChange = (m: Date) => setMonthFrom(m)
  const handleCalendarToMonthChange = (m: Date) => setMonthTo(m)
  const handleCancelClick = () => setDate({ ...initialDate })
  const handleApplyClick = () => setInitialDate({ ...date })

  return (
    <div className={cn("grid gap-0", className)} data-testid='date-filter'>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            onClick={handleCustomButtonClick}
            id="date"
            variant={"ghost"}
            data-testid='date-filter:custom-button'
            className={cn(
              "justify-start text-left font-normal",
              !date && "text-muted-foreground",
              triggerButtonClassName
            )}
          >
            <CalendarIcon className="size-4" />
            <span className="hidden xs:flex ml-1 self-center text-inherit leading-[inherit]">{buttonLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="f-full sm:w-auto p-0 mx-4" align="start">
          <div className={'w-full flex flex-col p-4'}>
            <div className={'w-full flex flex-row'}>
              <div className={'hidden xs:flex max-w-25 size-auto p-0 flex-col gap-0.5 justify-start mr-4'}>
                <ShortcutButtons presets={shortcutButtons} setDate={setDate} />
              </div>
              <div className={'p-0 flex flex-col sm:flex-row gap-4'}>
                <Calendar
                  data-testid='date-filter:calendar:from'
                  className='min-w-60'
                  fixedWeeks
                  mode="range"
                  numberOfMonths={1}
                  selected={date}
                  onSelect={setFromDate}
                  defaultMonth={date?.from}
                  month={monthFrom}
                  onMonthChange={handleCalendarFromMonthChange}
                  required
                  captionLayout={"dropdown"}
                  excludeDisabled
                  disabled={{
                    before: dateRangeStart,
                    after: disabledAfter
                  }}
                  startMonth={dateRangeStart}
                  endMonth={disabledAfter}
                />
                <Calendar
                  data-testid='date-filter:calendar:to'
                  className='min-w-60'
                  fixedWeeks
                  mode="range"
                  numberOfMonths={1}
                  selected={date}
                  onSelect={setToDate}
                  defaultMonth={date?.to}
                  month={monthTo}
                  onMonthChange={handleCalendarToMonthChange}
                  required
                  captionLayout={"dropdown"}
                  excludeDisabled
                  disabled={{
                    before: disabledBefore,
                    after: now
                  }}
                  startMonth={disabledBefore}
                  endMonth={now}
                />
              </div>
            </div>
            <div className={'mt-4 flex justify-between'}>
              <div>
                <Button variant={'outline'} onClick={resetDate}>Clear</Button>
              </div>
              <div className={'flex justify-end gap-2'}>
                <PopoverClose asChild>
                  <Button data-testid='date-filter:custom:cancel' variant={'outline'} onClick={handleCancelClick}>Cancel</Button>
                </PopoverClose>
                <PopoverClose asChild>
                  <Button data-testid='date-filter:custom:apply' variant={'default'} onClick={handleApplyClick}>Apply</Button>
                </PopoverClose>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}